// Neon Snake.io Arena Client (Responsive Screen, Boundaries & Instant Rebirth)
import { sounds } from '../sound.js';

export class SnakeArenaClient {
  constructor(socket, containerEl) {
    this.socket = socket;
    this.container = containerEl;
    this.snakes = {};
    this.foods = [];

    this.render();
    this.setupSocketEvents();
    this.setupControls();
  }

  render() {
    this.container.innerHTML = `
      <div class="snake-wrapper">
        <div class="snake-header">
          <div class="snake-meta">
            <h2>🐍 NEON SNAKE.IO ARENA</h2>
            <div class="snake-sub">Move mouse or touch to steer • Stay inside the electrified walls!</div>
          </div>
          <div id="snake-score-badge" class="snake-badge">SCORE: 0</div>
        </div>

        <div class="snake-canvas-box" style="position: relative;">
          <canvas id="snake-canvas" width="800" height="520"></canvas>

          <!-- Instant Reborn Overlay -->
          <div id="snake-death-modal" class="snake-reborn-modal modal-hidden">
            <h3>💀 YOUR SNAKE CRASHED!</h3>
            <p>Don't worry! Jump right back into the arena!</p>
            <button id="btn-snake-reborn" class="btn-reborn">
              <span>⚡</span> REBORN / RESPAWN
            </button>
          </div>
        </div>
      </div>
    `;

    this.canvas = document.getElementById('snake-canvas');
    this.ctx = this.canvas.getContext('2d');

    document.getElementById('btn-snake-reborn')?.addEventListener('click', () => {
      sounds.playSuccess();
      document.getElementById('snake-death-modal')?.classList.add('modal-hidden');
      this.socket.emit('snake_respawn');
    });
  }

  setupControls() {
    const steer = (e) => {
      const rect = this.canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const canvasX = (clientX - rect.left) * (this.canvas.width / rect.width);
      const canvasY = (clientY - rect.top) * (this.canvas.height / rect.height);

      const mySnake = this.snakes[this.socket.id];
      if (mySnake && mySnake.isAlive) {
        const head = mySnake.body[0];
        const angle = Math.atan2(canvasY - head.y, canvasX - head.x);
        this.socket.emit('snake_steer', { angle });
      }
    };

    this.canvas.addEventListener('mousemove', steer);
    this.canvas.addEventListener('touchmove', steer, { passive: false });
  }

  setupSocketEvents() {
    this.socket.on('snake_state', (data) => {
      this.snakes = data.snakes;
      this.foods = data.foods;

      const mySnake = this.snakes[this.socket.id];
      const scoreBadge = document.getElementById('snake-score-badge');
      if (scoreBadge && mySnake) {
        scoreBadge.innerText = `SCORE: ${mySnake.score}`;
      }

      this.draw();
    });

    this.socket.on('snake_died', (data) => {
      if (data.playerId === this.socket.id) {
        sounds.playExplosion();
        document.getElementById('snake-death-modal')?.classList.remove('modal-hidden');
      }
    });
  }

  draw() {
    if (!this.ctx) return;
    this.ctx.fillStyle = '#090d16';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Neon Electrified Boundary Wall
    this.ctx.strokeStyle = '#ef4444';
    this.ctx.shadowColor = '#ef4444';
    this.ctx.shadowBlur = 10;
    this.ctx.lineWidth = 4;
    this.ctx.strokeRect(3, 3, this.canvas.width - 6, this.canvas.height - 6);
    this.ctx.shadowBlur = 0;

    // Grid pattern
    this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.04)';
    this.ctx.lineWidth = 1;
    for (let x = 0; x < this.canvas.width; x += 40) {
      this.ctx.beginPath();
      this.ctx.moveTo(x, 0);
      this.ctx.lineTo(x, this.canvas.height);
      this.ctx.stroke();
    }
    for (let y = 0; y < this.canvas.height; y += 40) {
      this.ctx.beginPath();
      this.ctx.moveTo(0, y);
      this.ctx.lineTo(this.canvas.width, y);
      this.ctx.stroke();
    }

    // Food Pellets
    this.foods.forEach((f) => {
      this.ctx.fillStyle = f.color;
      this.ctx.shadowColor = f.color;
      this.ctx.shadowBlur = 8;
      this.ctx.beginPath();
      this.ctx.arc(f.x, f.y, 5.5, 0, Math.PI * 2);
      this.ctx.fill();
    });
    this.ctx.shadowBlur = 0;

    // Draw Snakes
    for (const id in this.snakes) {
      const s = this.snakes[id];
      if (!s.isAlive) continue;

      const isGhost = s.invulnFrames > 0;

      this.ctx.strokeStyle = s.color;
      this.ctx.shadowColor = s.color;
      this.ctx.shadowBlur = isGhost ? 20 : 12;
      this.ctx.lineWidth = 13;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.globalAlpha = isGhost ? 0.6 : 1.0;

      this.ctx.beginPath();
      s.body.forEach((pt, idx) => {
        if (idx === 0) this.ctx.moveTo(pt.x, pt.y);
        else this.ctx.lineTo(pt.x, pt.y);
      });
      this.ctx.stroke();
      this.ctx.globalAlpha = 1.0;
      this.ctx.shadowBlur = 0;

      // Snake Head & Name
      const head = s.body[0];
      this.ctx.fillStyle = '#ffffff';
      this.ctx.font = 'bold 11px Rajdhani, sans-serif';
      this.ctx.textAlign = 'center';
      this.ctx.fillText(`${s.name} (${s.score})`, head.x, head.y - 12);
    }
  }

  destroy() {
    this.socket.off('snake_state');
    this.socket.off('snake_died');
  }
}
